/* eslint-disable camelcase */
import { BenefitTypeOptions } from '@strata/constants';
import { DateInputFormik } from '@strata/react-date-time';
import { ErrorDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { Loading } from '@strata/react-loading-animation';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { CREATE_ROUTE } from 'services/constants';
import { parseValidationError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';
import { getPayerSegmentMap, mapPayerSegment } from 'services/PayerSegmentSupport';
import {
    loadPayerValues,
    packagePayerValues,
    payerValuesSchema,
} from 'services/ProductConfigSupport';

const activeTypes = [
    { name: 'Active', value: true },
    { name: 'Inactive', value: false },
];

const booleanOptions = [
    { name: 'Yes', value: true },
    { name: 'No', value: false },
];

const drugVersion = [
    { name: 'Use Product Default', value: null },
    { name: 'Brand', value: true },
    { name: 'Generic', value: false },
];

export const ProductSegmentConfigDetails = () => {
    const { segment_id, oid, benefit } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(`/edit-product-config/${oid}`);

    const isCreate = segment_id === CREATE_ROUTE;

    const [lastError, setLastError] = useState(null);
    const [isError, errorTitle, errorDetails] = parseValidationError(lastError);

    const config = useGet(
        !isCreate && `/reference/product_benefit_configs/edit/${segment_id}/${oid}/${benefit}`,
    );

    const payerSegmentsList = useGet('/reference/payer_segments');
    const payerSegmentsMap = getPayerSegmentMap(payerSegmentsList);
    const locations = useGet('/reference/product_benefit_configs/places');

    function clearError() {
        setLastError(null);
    }

    const notReadyForExisting = !isCreate && !config;

    if (!payerSegmentsList || !payerSegmentsMap || notReadyForExisting || !locations) {
        return <Loading />;
    }

    const places = locations.map((location) => ({
        name: location.description,
        value: location.code,
    }));

    const getPlaces = (items) =>
        items ? places.filter((place) => items.includes(place.value)) : [];

    const setPlaces = (items) => items?.map((item) => item.value) ?? [];

    return (
        <Formik
            initialValues={loadPayerValues({
                oid,
                segment_id: !isCreate ? segment_id : '',
                config: !isCreate ? config : {},
            })}
            validationSchema={payerValuesSchema}
            onSubmit={async (values) => {
                try {
                    const body = packagePayerValues(values);
                    await put(
                        `/reference/product_benefit_configs/${values.segment_id}/${values.oid}/${values.preferred_billing_type}`,
                        body,
                    );
                    SuccessToast({
                        message: 'Product payer configuration saved successfully.',
                    });

                    goHome();
                } catch (error) {
                    setLastError(error);
                }
            }}
        >
            {(formik) => {
                const disableSave = formik.isSubmitting || !(formik.isValid && formik.dirty);
                return (
                    <>
                        <ErrorDialog
                            dialogTitle={errorTitle}
                            isOpen={isError}
                            onSecondaryButtonClick={() => clearError()}
                            dialogContent={errorDetails}
                        />
                        <FormContainer
                            formTitle={`${isCreate ? 'Create' : 'Edit'} Product Payer Config`}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={disableSave}
                            onSecondaryButtonClick={goHome}
                            formLeftContent={
                                <>
                                    <TextInputFormik label="Product OID" id="oid" disabled />
                                    <SuggestSingleSelectFormik
                                        id="segment_id"
                                        label="Payer Segment"
                                        values={(payerSegmentsList ?? []).map(mapPayerSegment)}
                                        onChange={(newValue) =>
                                            formik.setFieldValue('segment_id', newValue.segment_id)
                                        }
                                        selectedItem={
                                            formik.values?.segment_id &&
                                            mapPayerSegment(
                                                payerSegmentsMap?.[formik.values.segment_id],
                                            )
                                        }
                                        itemsEqual="segment_id"
                                        disabled={!isCreate}
                                        required
                                    />
                                    <SuggestMultiSelectFormik
                                        id="valid_sites_of_care"
                                        label="Valid Sites of Care"
                                        values={places}
                                        onChange={(newItems) =>
                                            formik.setFieldValue(
                                                'valid_sites_of_care',
                                                setPlaces(newItems),
                                            )
                                        }
                                        selectedItems={getPlaces(formik.values.valid_sites_of_care)}
                                    />
                                    <SuggestSingleSelectFormik
                                        id="preferred_billing_type"
                                        label="Preferred Billing Type"
                                        required
                                        values={BenefitTypeOptions}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'preferred_billing_type',
                                                newValue.value,
                                            )
                                        }
                                        selectedItem={BenefitTypeOptions.find(
                                            ({ value }) =>
                                                value === formik.values.preferred_billing_type,
                                        )}
                                        itemsEqual="value"
                                        disabled={!isCreate}
                                    />
                                    <SuggestSingleSelectFormik
                                        id="is_active"
                                        label="Active?"
                                        values={activeTypes}
                                        selectedItem={activeTypes.find(
                                            (b) => b.value === formik.values.is_active,
                                        )}
                                        disabled
                                    />
                                    <DateInputFormik id="start_date" label="Start Date" required />
                                    <DateInputFormik
                                        id="end_date"
                                        label="End Date"
                                        minDate={dayjs(formik.values.start_date)
                                            .startOf('day')
                                            .toDate()}
                                        helperText="Product will be active until 11:59PM on the selected end date."
                                    />
                                    <SuggestSingleSelectFormik
                                        id="use_drug_qty_defaults"
                                        label="Use Drug Quantity and Days Supply Defaults?"
                                        required
                                        values={booleanOptions}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'use_drug_qty_defaults',
                                                newValue.value,
                                            )
                                        }
                                        selectedItem={booleanOptions.find(
                                            (b) => b.value === formik.values.use_drug_qty_defaults,
                                        )}
                                    />
                                    <SuggestSingleSelectFormik
                                        id="is_brand_default"
                                        label="Product Type Default"
                                        required
                                        values={drugVersion}
                                        onChange={(newValue) =>
                                            formik.setFieldValue('is_brand_default', newValue.value)
                                        }
                                        selectedItem={drugVersion.find(
                                            (b) => b.value === formik.values.is_brand_default,
                                        )}
                                    />
                                    <SuggestSingleSelectFormik
                                        id="prior_auth_required"
                                        label="Prior Authorization Required?"
                                        required
                                        values={booleanOptions}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'prior_auth_required',
                                                newValue.value,
                                            )
                                        }
                                        selectedItem={booleanOptions.find(
                                            (b) => b.value === formik.values.prior_auth_required,
                                        )}
                                    />
                                    <TextInputFormik
                                        label="Last Update"
                                        id="last_update"
                                        disabled
                                    />
                                </>
                            }
                        />
                    </>
                );
            }}
        </Formik>
    );
};
